/**
 * DEVELOPER DOCUMENTATION
 *
 * Include your custom JavaScript here.
 *
 * The theme Focal has been developed to be easily extensible through the usage of a lot of different JavaScript
 * events, as well as the usage of custom elements (https://developers.google.com/web/fundamentals/web-components/customelements)
 * to easily extend the theme and re-use the theme infrastructure for your own code.
 *
 * The technical documentation is summarized here.
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A VARIANT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a the user has changed the variant in a selector. The target get you the form
 * that triggered this event.
 *
 * Example:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   let variant = event.detail.variant; // Gives you access to the whole variant details
 *   let form = event.target;
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * MANUALLY CHANGE A VARIANT
 * ------------------------------------------------------------------------------------------------------------
 *
 * You may want to manually change the variant, and let the theme automatically adjust all the selectors. To do
 * that, you can get the DOM element of type "<product-variants>", and call the selectVariant method on it with
 * the variant ID.
 *
 * Example:
 *
 * const productVariantElement = document.querySelector('product-variants');
 * productVariantElement.selectVariant(12345);
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A NEW VARIANT IS ADDED TO THE CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a variant is added to the cart through a form selector (product page, quick
 * view...). This event DOES NOT include any change done through the cart on an existing variant. For that,
 * please refer to the "cart:updated" event.
 *
 * Example:
 *
 * document.addEventListener('variant:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN THE CART CONTENT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever the cart content has changed (if the quantity of a variant has changed, if a variant
 * has been removed, if the note has changed...). This event will also be emitted when a new variant has been
 * added (so you will receive both "variant:added" and "cart:updated"). Contrary to the variant:added event,
 * this event will give you the complete details of the cart.
 *
 * Example:
 *
 * document.addEventListener('cart:updated', function(event) {
 *   var cart = event.detail.cart; // Get the updated content of the cart
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * REFRESH THE CART/MINI-CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * If you are adding variants to the cart and would like to instruct the theme to re-render the cart, you cart
 * send the cart:refresh event, as shown below:
 *
 * document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *   bubbles: true
 * }));
 *
 * ------------------------------------------------------------------------------------------------------------
 * USAGE OF CUSTOM ELEMENTS
 * ------------------------------------------------------------------------------------------------------------
 *
 * Our theme makes extensive use of HTML custom elements. Custom elements are an awesome way to extend HTML
 * by creating new elements that carry their own JavaScript for adding new behavior. The theme uses a large
 * number of custom elements, but the two most useful are drawer and popover. Each of those components add
 * a "open" attribute that you can toggle on and off. For instance, let's say you would like to open the cart
 * drawer, whose id is "mini-cart", you simply need to retrieve it and set its "open" attribute to true (or
 * false to close it):
 *
 * document.getElementById('mini-cart').open = true;
 *
 * Thanks to the power of custom elements, the theme will take care automagically of trapping focus, maintaining
 * proper accessibility attributes...
 *
 * If you would like to create your own drawer, you can re-use the <drawer-content> content. Here is a simple
 * example:
 *
 * // Make sure you add "aria-controls", "aria-expanded" and "is" HTML attributes to your button:
 * <button type="button" is="toggle-button" aria-controls="id-of-drawer" aria-expanded="false">Open drawer</button>
 *
 * <drawer-content id="id-of-drawer">
 *   Your content
 * </drawer-content>
 *
 * The nice thing with custom elements is that you do not actually need to instantiate JavaScript yourself: this
 * is done automatically as soon as the element is inserted to the DOM.
 *
 * ------------------------------------------------------------------------------------------------------------
 * THEME DEPENDENCIES
 * ------------------------------------------------------------------------------------------------------------
 *
 * While the theme tries to keep outside dependencies as small as possible, the theme still uses third-party code
 * to power some of its features. Here is the list of all dependencies:
 *
 * "vendor.js":
 *
 * The vendor.js contains required dependencies. This file is loaded in parallel of the theme file.
 *
 * - custom-elements polyfill (used for built-in elements on Safari - v1.0.0): https://github.com/ungap/custom-elements
 * - web-animations-polyfill (used for polyfilling WebAnimations on Safari 12, this polyfill will be removed in 1 year - v2.3.2): https://github.com/web-animations/web-animations-js
 * - instant-page (v5.1.0): https://github.com/instantpage/instant.page
 * - tocca (v2.0.9); https://github.com/GianlucaGuarini/Tocca.js/
 * - seamless-scroll-polyfill (v2.0.0): https://github.com/magic-akari/seamless-scroll-polyfill
 *
 * "flickity.js": v2.2.0 (with the "fade" package). Flickity is only loaded on demand if there is a product image
 * carousel on the page. Otherwise it is not loaded.
 *
 * "photoswipe": v4.1.3. PhotoSwipe is only loaded on demand to power the zoom feature on product page. If the zoom
 * feature is disabled, then this script is never loaded.
 */
/*
 * Back-to-top bootstrap. The button is normally server-rendered by snippets/back-to-top.liquid
 * (see layout/theme.liquid); this only rebuilds it when the HTML arrived without it — i.e. pages
 * served from Shopify's full-page cache that predate the snippet. Guarded, so a server-rendered
 * button is left alone. Safe to delete once every cached page has expired.
 */
(() => {
  // Same theme directory as this very script - survives theme copies where /t/<n>/ changes.
  const iconUrl = (document.currentScript?.src || '').replace(/custom\.js[^/]*$/, 'back-to-top-icon.png');

  const boot = () => {
    if (document.querySelector('.back-to-top') || !iconUrl) return;

    const style = document.createElement('style');
    style.textContent = `
      .back-to-top { position: fixed; inset-inline-end: 24px; bottom: 32px; z-index: 5; display: block; width: 48px; height: 48px; padding: 0; border: none; cursor: pointer; background: transparent; opacity: 1; transition: opacity .25s ease, transform .25s ease; }
      .back-to-top[hidden] { display: block; opacity: 0; pointer-events: none; transform: translateY(8px); }
      .back-to-top img { display: block; width: 100%; height: 100%; border-radius: 50%; filter: drop-shadow(0 4px 10px rgba(0,0,0,0.12)); }
      .back-to-top:hover { transform: translateY(-2px); }
      @media screen and (max-width: 740px) { .back-to-top { inset-inline-end: 16px; bottom: 20px; width: 44px; height: 44px; } }
    `;
    document.head.appendChild(style);

    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'back-to-top';
    btn.setAttribute('aria-label', 'Back to top');
    btn.hidden = true;
    const img = document.createElement('img');
    img.src = iconUrl;
    img.alt = '';
    btn.appendChild(img);
    document.body.appendChild(btn);

    const sync = () => { btn.hidden = window.scrollY < window.innerHeight; };
    window.addEventListener('scroll', sync, { passive: true });
    sync();

    btn.addEventListener('click', () => window.scrollTo({ top: 0, behavior: 'smooth' }));
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();

/*
 * Scroll-to-close for the desktop mega menu. Focal opens the header dropdown on
 * hover and keeps it open via :hover / aria-expanded. On a sticky header the
 * pointer stays over the trigger while the wheel scrolls the page, so the panel
 * never closes on its own. This closes any open dropdown the moment the page
 * scrolls, then lets a genuine re-hover (crossing into a nav item) reopen it.
 */
(() => {
  const style = document.createElement('style');
  style.textContent = `
    @media screen and (min-width: 1000px) {
      .header__linklist-item.nav-scroll-closed .nav-dropdown {
        visibility: hidden !important;
        opacity: 0 !important;
        pointer-events: none !important;
      }
    }`;
  document.head.appendChild(style);

  const closeOpen = () => {
    document.querySelectorAll('.header__linklist-item').forEach((li) => {
      const link = li.querySelector('.header__linklist-link');
      const dd = li.querySelector('.nav-dropdown');
      const open = (link && link.getAttribute('aria-expanded') === 'true') || (dd && !dd.hasAttribute('hidden'));
      if (open) li.classList.add('nav-scroll-closed');
    });
  };

  let ticking = false;
  window.addEventListener('scroll', () => {
    if (ticking) return;
    ticking = true;
    requestAnimationFrame(() => { closeOpen(); ticking = false; });
  }, { passive: true });

  // Emulate per-item mouseenter via delegation (survives Focal's sticky-header
  // cloning): clear the suppression flag only when the pointer crosses into a
  // *different* nav item, so the item scrolled-away-from stays closed while the
  // pointer rests on it, and any sibling opens normally on hover.
  let lastItem = null;
  document.addEventListener('mouseover', (event) => {
    const li = event.target.closest && event.target.closest('.header__linklist-item');
    if (li !== lastItem) {
      lastItem = li;
      if (li) li.classList.remove('nav-scroll-closed');
    }
  }, true);
})();

/*
 * Site-wide desktop scaling: reproduce the 1920 @ 100% layout at every desktop width.
 *
 * zoom = viewport / 1920, per section. Inside a zoomed section the layout space is exactly 1920 CSS
 * px whatever the window measures, so each section renders precisely as it does on the 1920 monitor
 * the design was approved on -- smaller on laptops, larger on 2K/4K -- and OS display scaling
 * cancels out: a 1920 monitor at 125% reports 1536, we scale by 0.8, the OS scales back by 1.25,
 * net 1.0. JS because CSS cannot divide a length by a length (zoom: calc(100vw / 1920px) is
 * invalid); clientWidth because the sections are laid out in the space that excludes the scrollbar.
 *
 * Header group, hero, content and footer all scale together. Every alignment bug this branch fought
 * came from two rulers -- one element left on the real-viewport ruler while its neighbour sat on the
 * 1920 ruler -- and their edges can only agree at one width. That is also why the old wide-screen
 * patches (the 80px gutter floor for the hero arrow, the 1921px header/hero insets, the
 * 100vw - 224px container cap) are gone: each was a compensation for exactly that split, and against
 * a zoomed page each re-breaks what it fixed. Overlays (mini-cart, newsletter modal) stay unscaled
 * on purpose -- they fit the viewport, not the page.
 *
 * The container variables are pinned to the constants the theme computes at 1920 (outer 160px,
 * inner 1600px) rather than left to their formulas: vw units inside a zoomed box still report the
 * real viewport, so the formulas keep answering for the wrong ruler.
 *
 * Below 1200 the responsive layout stands -- this formula would shrink a phone to ~3px type.
 */
(() => {
  const BASE = 1920, MIN = 1200;
  const SCALED = '#main > .shopify-section, .shopify-section-group-header-group, .shopify-section-group-footer-group';
  const PINNED_VARS = ['--container-outer-width', '--container-outer-margin', '--container-inner-width'];
  const PINNED_VALUES = ['160px', '160px', '1600px'];

  const apply = () => {
    const vw = document.documentElement.clientWidth;
    const z = vw / BASE;
    const on = vw >= MIN;

    document.querySelectorAll(SCALED).forEach((sec) => {
      if (!on) {
        sec.style.removeProperty('zoom');
        sec.style.removeProperty('--zoom-factor');
        PINNED_VARS.forEach((v) => sec.style.removeProperty(v));
        return;
      }
      sec.style.zoom = z;
      sec.style.setProperty('--zoom-factor', z);
      PINNED_VARS.forEach((v, i) => sec.style.setProperty(v, PINNED_VALUES[i]));
    });

    // The header measures its own geometry (--header-wrapper-width, --mega-trigger-offset) through
    // a ResizeObserver on .header__wrapper -- which is max-content and caps at the same 1680 in the
    // zoomed space as out of it, so applying the zoom changes every measured position without ever
    // firing the observer. Re-measure explicitly; the method divides by the section's zoom itself.
    const storeHeader = document.querySelector('store-header');
    if (storeHeader && typeof storeHeader.setHeaderWrapperWidth === 'function') {
      storeHeader.setHeaderWrapperWidth();
    }
  };

  const schedule = (() => {
    let queued = false;
    return () => {
      if (queued) return;
      queued = true;
      requestAnimationFrame(() => { queued = false; apply(); });
    };
  })();

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', apply);
  } else {
    apply();
  }
  window.addEventListener('resize', schedule);
  // The theme editor re-creates section markup; the inline styles go with it.
  document.addEventListener('shopify:section:load', apply);
  document.addEventListener('shopify:section:unload', apply);
})();
